<script setup>
import {ref, watch, computed, onMounted, onBeforeUnmount} from 'vue';
import {useI18n} from 'vue-i18n';
import FilmCutForm from '@/components/forms/FilmCutForm.vue';
import PopOverNoteItem from '@/components/items/PopOverNoteItem.vue';
import {Fancybox} from '@fancyapps/ui';
import '@fancyapps/ui/dist/fancybox/fancybox.css';
import Swal from 'sweetalert2';
import axios from '@/axios.config.js';
import {log} from "@/utils/clientLogger.js";
import helpers from '@root/helpers.js';

const {t} = useI18n();

const props = defineProps({
  modelValue: Array,
  canEditProgram: {
    type: Boolean,
    default: false
  }
});

const emit = defineEmits(['update:modelValue', 'cutsChange', 'programChange', 'cutRemoved']);

const assemblyPrograms = helpers.assemblyPrograms;
const programLabel = (value) => helpers.getAssemblyProgramLabel(value);

function genCutId() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return 'cut_' + crypto.randomUUID();
  return 'cut_' + Date.now() + '_' + Math.random().toString(36).slice(2);
}

function setCutProgram(index, event) {
  const cut = localModelValue.value[index];
  if (!cut) return;

  const value = event.target.value;
  const oldProgram = cut.program || '';
  const cutId = cut.id ?? genCutId();
  localModelValue.value.splice(index, 1, {...cut, id: cutId, program: value});

  emit('update:modelValue', [...localModelValue.value]);
  emit('programChange', {cutId, program: value, oldProgram});
}

const localModelValue = ref([]);

function normalizePath(raw) {
  if (!raw) return '';

  if (raw.startsWith('http://') || raw.startsWith('https://')) {
    return raw;
  }

  const cleaned = raw.replace(/\\/g, '/').replace(/[^\x20-\x7E]/g, '');
  return decodeURIComponent(cleaned.startsWith('/') ? cleaned : '/' + cleaned);
}

watch(() => props.modelValue, (newVal) => {
  localModelValue.value = newVal.map(cut => ({
    ...cut,
    path: normalizePath(cut.path)
  }));
});

const filmCutsRef = ref(null);

function getCutData(data) {
  if (data.index === null || data.index === undefined || isNaN(Number(data.index))) {
    const newIndex = localModelValue.value.length;
    localModelValue.value.push({...data, id: data.id ?? genCutId(), index: newIndex});
  } else {
    const index = Number(data.index);
    const oldData = localModelValue.value[index];

    const mergedData = {
      ...oldData,
      ...data,
      path: data.path,
      image: data.path,
      url: data.path,
      patternId: oldData.patternId,
      index: index
    };

    localModelValue.value.splice(index, 1, mergedData);
  }

  emit('update:modelValue', [...localModelValue.value]);
  emit('cutsChange', 'cuts', localModelValue.value);
}

function roundFilmSize(num) {
  const parsed = parseFloat(num);
  if (!parsed) return 0;
  if (parsed === 0.76) return 0.75;
  if (parsed === 1.83) return 1.8;
  return Math.round(parsed * 10) / 10;
}

async function downloadAllImagesAsZip() {
  const paths = localModelValue.value.map(item => item.path).filter(Boolean); // тільки валідні шляхи

  if (paths.length === 0) {
    log.error('View:FilmCutsEditor:downloadAllImagesAsZip', {'message': 'No files to download!'});
    return;
  }

  try {
    const response = await axios.post('/api/download-cuts-zip', {paths}, {
      responseType: 'blob'
    });

    const blob = new Blob([response.data], {type: 'application/zip'});
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'film_cuts.zip';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  } catch (err) {
    log.error('View:FilmCutsEditor:downloadAllImagesAsZip', {'error': err});
  }
}

function removeCut(index) {
  const cutData = localModelValue.value[index];
  if (!cutData) return;

  const title = t('asks.deleteCut');
  const confirmButtonText = t('buttons.confirm');
  const cancelButtonText = t('buttons.cancel');
  const successMessage = t('messages.cutDeleted');

  Swal.fire({
    title,
    icon: 'warning',
    showCancelButton: true,
    confirmButtonText,
    cancelButtonText,
  }).then((result) => {
    if (result.isConfirmed) {
      if (cutData.imageId) {
        emit('cutRemoved', cutData.imageId);
      }
      localModelValue.value.splice(index, 1);
      Swal.fire(successMessage, '', 'success');
      emit('update:modelValue', [...localModelValue.value]);
      emit('cutsChange', 'cuts', localModelValue.value);
    }
  });
}

const totalFilmSize = computed(() => {
  let total = 0;
  localModelValue.value.forEach(item => {
    item.filmSize = roundFilmSize(item.width) * roundFilmSize(item.height);
    total += item.filmSize;
  });
  return total.toFixed(2);
});

const fancyBoxOptions = {
  Carousel: {infinite: false},
  Toolbar: {
    items: {
      customPrint: {
        tpl: '<button class="f-button">🖨</button>',
        click: () => {
          const image = Fancybox.getSlide().src;
          const printWindow = window.open('', '_blank', 'width=500,height=500');
          printWindow.document.write(`<html><head><title>${t('titles.printImage')}</title></head><body><img src="${image}" onload="window.print();window.close()" /></body></html>`);
          printWindow.document.close();
        }
      }
    },
    display: {
      left: ['infobar'],
      middle: [],
      right: ['customPrint', 'slideshow', 'download', 'thumbs', 'close']
    }
  }
};

onMounted(() => {
  Fancybox.bind(filmCutsRef.value, '[data-fancybox="cutImageZoom"]', fancyBoxOptions);
});

onBeforeUnmount(() => {
  Fancybox.unbind(filmCutsRef.value);
});
</script>


<template>
  <div id="filmCuts" ref="filmCutsRef" class="row align-items-center justify-content-start mt-3">
    <div class="d-flex flex-column flex-md-row gap-md-3 mb-3 mb-md-0 align-items-md-center">
      <h5>{{ t('labels.filmCutsForOrder') }}</h5>
      <button type="button" class="btn btn-outline-secondary btn-sm ms-md-2" @click="downloadAllImagesAsZip">
        {{ t('buttons.downloadAllCuts') }}
      </button>
    </div>
    <div
        v-for="(item, index) in localModelValue"
        :key="index"
        class="col-md-4 col-6 mt-3 mb-4"
    >
      <div
          class="filmCutsItem w-100 d-flex align-items-center justify-content-center"
          :style="{ backgroundImage: `url('${item.path ?? ''}')` }"
      >
        <strong
            class="cutIndex"
            data-bs-toggle="modal"
            data-bs-target="#editCutModal"
            :data-index="index"
            :data-width="item.width"
            :data-height="item.height"
            :data-note="item.note"
            :data-image-id="item.imageId"
            :data-path="item.path ?? null"
        >
          {{ index + 1 }}
        </strong>
        <span @click="removeCut(index)" class="btn-close btn"></span>
        <span class="filmWidth cut-item_size"><strong>{{ item.width }}</strong></span>
        <span class="filmHeight cut-item_size"><strong>{{ item.height }}</strong></span>
        <div class="cutData p-1">
          <ul class="p-0 m-0">
            <li><strong>{{ t('labels.filmWidth') }}:</strong> {{ item.width }}</li>
            <li><strong>{{ t('labels.filmLength') }}:</strong> {{ item.height }}</li>
          </ul>
        </div>
        <a
            v-if="item.path"
            :href="item.path"
            :data-fancybox="'cutImageZoom'"
            :data-src="item.path"
            class="cutImageZoom position-absolute"
        >
          <svg xmlns="http://www.w3.org/2000/svg" class="icon icon-tabler icon-tabler-photo-search" width="18" height="18" viewBox="0 0 24 24" stroke-width="1.5" stroke="#9e9e9e" fill="none" stroke-linecap="round" stroke-linejoin="round">
            <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
            <path d="M15 8h.01"/>
            <path d="M11.5 21h-5.5a3 3 0 0 1 -3 -3v-12a3 3 0 0 1 3 -3h12a3 3 0 0 1 3 3v5.5"/>
            <path d="M18 18m-3 0a3 3 0 1 0 6 0a3 3 0 1 0 -6 0"/>
            <path d="M20.2 20.2l1.8 1.8"/>
            <path d="M3 16l5 -5c.928 -.893 2.072 -.893 3 0l2 2"/>
          </svg>
        </a>
        <PopOverNoteItem v-if="item.note" v-model="item.note"/>
      </div>

      <div class="cut-program">
        <select v-if="canEditProgram"
                class="form-select form-select-sm"
                :value="item.program || ''"
                @change="setCutProgram(index, $event)">
          <option value="">{{ t('labels.assemblyProgramSelect', 'Оберіть програму збірки') }}</option>
          <option v-for="program in assemblyPrograms" :key="program.value" :value="program.value">
            {{ program.label }}
          </option>
        </select>
        <span v-else-if="item.program" class="badge bg-primary text-white w-100 text-truncate">
          {{ programLabel(item.program) }}
        </span>
      </div>
    </div>

    <FilmCutForm @getCutData="getCutData"/>

    <div class="col-12">
      <span class="mt-3 badge bg-info-subtle text-dark col-auto p-2">
        {{ t('labels.totalFilmArea') }}: <span class="ps-2">{{ totalFilmSize }} М<sup>2</sup></span>
      </span>
    </div>
  </div>
</template>

<style lang="scss">
.filmCutsItem {
  border: 1px solid black;
  height: 115px;
  position: relative;
  background-size: contain;
  background-repeat: no-repeat;
  background-position: center;

  .btn-close {
    position: absolute;
    right: 5px;
    top: 5px;
  }

  .cut-item_size {
    background-color: transparent;
    color: #1b1a1a;
    font-size: 10px;
    padding: 5px 2px;
  }

  .filmWidth {
    left: -20px;
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    white-space: nowrap;
    writing-mode: vertical-lr;

    &:before {
      content: "  ⤌  ";
      padding: 7px;
    }

    &:after {
      content: "  ⤏  ";
      padding: 7px;
    }
  }

  .filmHeight {
    bottom: -25px;
    left: 50%;
    position: absolute;
    transform: translateX(-50%);
    white-space: nowrap;

    &:before {
      content: "  ⤌  ";
      padding-right: 15px;
    }

    &:after {
      content: "  ⤏  ";
      padding-left: 15px;
    }
  }

  .cutIndex {
    background-color: red;
    padding: 10px;
    cursor: pointer;
    width: 30px;
    height: 30px;
    display: flex;
    justify-content: center;
    align-items: center;
    background: var(--bs-tertiary-color);
    color: #fff;
  }

  .cutData {
    position: absolute;
    left: 10px;
    top: 0;
    background-color: rgba(255, 255, 255, 0.29);

    ul {
      list-style: none;
      font-size: 12px;
    }
  }

  .cutImageZoom {
    right: 10px;
    bottom: 10px;
  }

}

.cut-program {
  // Відступ, щоб селект не перекривав підпис висоти (.filmHeight), який виступає під карткою
  margin-top: 30px;
}

@media (max-width: 767.98px) {
  #filmCuts > .col-6 {
    padding-left: 26px;
  }
}


</style>